import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const api = axios.create({
  baseURL: `${API_URL}/api`,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests (fallback when cookies don't work)
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('pyrostock_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  // Let axios set Content-Type automatically for FormData
  if (config.data instanceof FormData) {
    delete config.headers['Content-Type'];
  }
  return config;
});

/**
 * Use native fetch() for blob downloads to avoid XMLHttpRequest.responseText
 * conflicts with platform monitoring scripts.
 */
export async function fetchBlob(method, path, body = null) {
  const token = localStorage.getItem('pyrostock_token');
  const opts = {
    method,
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
  };
  if (token) opts.headers['Authorization'] = `Bearer ${token}`;
  if (body) opts.body = JSON.stringify(body);
  const res = await fetch(`${API_URL}/api${path}`, opts);
  if (!res.ok) {
    const text = await res.text().catch(() => 'Erreur serveur');
    throw new Error(text);
  }
  const blob = await res.blob();
  return { data: blob };
}

// Products API
export const productsApi = {
  getAll: (params) => api.get('/products', { params }),
  getById: (id) => api.get(`/products/${id}`),
  create: (data) => api.post('/products', data),
  update: (id, data) => api.put(`/products/${id}`, data),
  delete: (id, force = false) => api.delete(`/products/${id}${force ? '?force=true' : ''}`),
  uploadPhoto: (id, formData) => api.post(`/products/${id}/photo`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  getPhotos: (id) => api.get(`/products/${id}/photos`),
};

// Depots API
export const depotsApi = {
  getAll: () => api.get('/depots'),
  getById: (id) => api.get(`/depots/${id}`),
  create: (data) => api.post('/depots', data),
  update: (id, data) => api.put(`/depots/${id}`, data),
  delete: (id) => api.delete(`/depots/${id}`),
};

// Zones API
export const zonesApi = {
  getAll: (params) => api.get('/zones', { params }),
  create: (data) => api.post('/zones', data),
};

// Locations API
export const locationsApi = {
  getAll: (params) => api.get('/locations', { params }),
  getById: (id) => api.get(`/locations/${id}`),
  create: (data) => api.post('/locations', data),
};

// Stock API
export const stockApi = {
  getAll: (params) => api.get('/stock', { params }),
  getMovements: (params) => api.get('/stock/movements', { params }),
  createMovement: (data) => api.post('/stock/movements', data),
  availability: (productIds) => api.post('/stock/availability', { product_ids: productIds }),
  getForecast: () => api.get('/stock/forecast'),
};

// Lots API
export const lotsApi = {
  getAll: (params) => api.get('/lots', { params }),
  create: (data) => api.post('/lots', data),
};

// Boxes API
export const boxesApi = {
  getAll: (params) => api.get('/boxes', { params }),
  getById: (id) => api.get(`/boxes/${id}`),
  create: (data) => api.post('/boxes', data),
};

// Purchase Orders API
export const purchaseOrdersApi = {
  getAll: (params) => api.get('/orders/purchase', { params }),
  getById: (id) => api.get(`/orders/purchase/${id}`),
  create: (data) => api.post('/orders/purchase', data),
};

// Receptions API
export const receptionsApi = {
  getAll: () => api.get('/receptions'),
  create: (data) => api.post('/receptions', data),
  delete: (id) => api.delete(`/receptions/${id}`),
};

// Preparations API
export const preparationsApi = {
  getAll: (params) => api.get('/preparations', { params }),
  getById: (id) => api.get(`/preparations/${id}`),
  create: (data) => api.post('/preparations', data),
  update: (id, data) => api.put(`/preparations/${id}`, data),
  delete: (id) => api.delete(`/preparations/${id}`),
  validate: (id, items) => api.put(`/preparations/${id}/validate`, items),
  return: (id, items) => api.put(`/preparations/${id}/return`, items),
  duplicate: (id) => api.post(`/preparations/${id}/duplicate`),
  recapPdf: (id) => fetchBlob('GET', `/preparations/${id}/recap-pdf`),
  pickingPdf: (id) => fetchBlob('GET', `/preparations/${id}/picking-pdf`),
};

// Inventories API
export const inventoriesApi = {
  getAll: () => api.get('/inventories'),
  create: (data) => api.post('/inventories', data),
  validate: (id) => api.put(`/inventories/${id}/validate`),
  delete: (id) => api.delete(`/inventories/${id}`),
};

// Alerts API
export const alertsApi = {
  getAll: (params) => api.get('/alerts', { params }),
  markRead: (id) => api.put(`/alerts/${id}/read`),
  markAllRead: () => api.put('/alerts/read-all'),
  getStockAlerts: () => api.get('/alerts/stock'),
  setThreshold: (productId, threshold) => api.put(`/products/${productId}/alert-threshold`, { min_stock_threshold: threshold }),
};

// Users API
export const usersApi = {
  getAll: () => api.get('/users'),
  update: (id, data) => api.put(`/users/${id}`, data),
  delete: (id) => api.delete(`/users/${id}`),
};

// Clients API
export const clientsApi = {
  getAll: (params) => api.get('/clients', { params }),
  getById: (id) => api.get(`/clients/${id}`),
  create: (data) => api.post('/clients', data),
  update: (id, data) => api.put(`/clients/${id}`, data),
  delete: (id) => api.delete(`/clients/${id}`),
};

// Dashboard API
export const dashboardApi = {
  getStats: () => api.get('/dashboard/stats'),
};

// Documents API
export const documentsApi = {
  generateTransport: (data) => fetchBlob('POST', '/documents/transport', data),
};

// Exports API
export const exportsApi = {
  exportStock: (params) => {
    const query = new URLSearchParams(params).toString();
    return fetchBlob('GET', `/exports/stock${query ? '?' + query : ''}`);
  },
  exportHomologation: () => fetchBlob('GET', '/exports/homologation'),
};

// Search API
export const searchApi = {
  global: (q) => api.get('/search', { params: { q } }),
};

// Scan API
export const scanApi = {
  process: (data) => api.post('/scan', data),
};

// Assemblies API
export const assembliesApi = {
  getAll: (params) => api.get('/assemblies', { params }),
  getById: (id) => api.get(`/assemblies/${id}`),
  create: (data) => api.post('/assemblies', data),
  update: (id, data) => api.put(`/assemblies/${id}`, data),
  delete: (id) => api.delete(`/assemblies/${id}`),
  fabricate: (id, data) => api.post(`/assemblies/${id}/fabricate`, data),
};

// Fabrications API
export const fabricationsApi = {
  getAll: (params) => api.get('/fabrications', { params }),
};

// QR Code API
export const qrcodeApi = {
  generate: (type, id) => `${API_URL}/api/qrcode/${type}/${id}`,
  generateLabel: (boxId) => `${API_URL}/api/labels/box/${boxId}`,
};

// Audit Logs API
export const auditLogsApi = {
  getAll: (params) => api.get('/audit-logs', { params }),
};

// Suppliers API
export const suppliersApi = {
  getAll: (params) => api.get('/suppliers', { params }),
  create: (data) => api.post('/suppliers', data),
  update: (id, data) => api.put(`/suppliers/${id}`, data),
  delete: (id) => api.delete(`/suppliers/${id}`),
};

// Import API
export const importApi = {
  preview: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/import/preview', formData);
  },
  execute: (file, target, mapping) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('target', target);
    formData.append('mapping', JSON.stringify(mapping));
    return api.post('/import/execute', formData);
  },
};

// Product History API
export const productHistoryApi = {
  get: (id) => api.get(`/products/${id}/history`),
};

// Artifice Types API
export const artificeTypesApi = {
  getAll: () => api.get('/artifice-types'),
  create: (name) => api.post('/artifice-types', { name }),
  delete: (name) => api.delete(`/artifice-types/${encodeURIComponent(name)}`),
};

// Stock Report API
export const stockReportApi = {
  generate: (data) => fetchBlob('POST', '/documents/stock-report', data),
};

// Labels API
export const labelsApi = {
  generate: (items) => fetchBlob('POST', '/labels/generate', { items }),
  preview: (productId) => fetchBlob('POST', '/labels/preview', { product_id: productId }),
};

export default api;
